import type { ExecutionEventRow } from '../../executionOperations/types';
import type { ExecutionProgressHydration } from '../../executionOperations/types';
import { isLoadingStop } from '../../executionOperations/executionHelpers';
import { getExecutionStatusLabel, normalizeOperationalState } from './statusFacade';
import { deriveRouteOperationalStatus } from './routeOperationalStatus';
import type { ExecutionIncident } from './incidentFacade';
import { formatIncidentSummary, getIncidentTypeLabel } from './incidentFacade';
import type { StopOperationalContext } from './operationalContextFacade';
import { formatCargoLinesList } from './operationalContextFacade';

export type OperationalTimelineEntry = {
  key: string;
  label: string;
  at: string | null;
  kind: 'milestone' | 'loading' | 'unloading' | 'transit' | 'incident' | 'system';
  completed: boolean;
  inProgress: boolean;
  detail?: string;
  address?: string | null;
  /** Extra operational lines (application, client, cargo). */
  contextLines?: string[];
};

function timelineContextLines(ctx: StopOperationalContext | undefined, cargoLines?: boolean): string[] {
  if (!ctx) return [];
  const lines: string[] = [];
  if (ctx.applicationLabel) lines.push(ctx.applicationLabel);
  if (ctx.clientName) lines.push(ctx.clientName);
  if (ctx.consigneeName && ctx.consigneeName !== ctx.clientName) lines.push(ctx.consigneeName);
  if (cargoLines && ctx.cargoLines.length > 0) {
    lines.push(...formatCargoLinesList(ctx.cargoLines));
  }
  return lines;
}

export function buildOperationalTimelineFromHydration(
  hydration: ExecutionProgressHydration,
  incidents: ExecutionIncident[] = [],
  stopContextMap: Map<string, StopOperationalContext> = new Map()
): OperationalTimelineEntry[] {
  const entries: OperationalTimelineEntry[] = [];
  const routeStatus = deriveRouteOperationalStatus(hydration, incidents);

  entries.push({
    key: 'created',
    label: 'Маршрут создан',
    at: null,
    kind: 'milestone',
    completed: true,
    inProgress: false
  });

  for (const stop of hydration.stops.filter((s) => isLoadingStop(s.type))) {
    const state = normalizeOperationalState(stop.executionState);
    const ctx = stopContextMap.get(stop.executionStopUid);
    if (stop.loadingStartedAt || state !== 'loading_pending') {
      entries.push({
        key: `load-start-${stop.executionStopUid}`,
        label: 'Начата погрузка',
        at: stop.loadingStartedAt ?? stop.arrivedAt,
        kind: 'loading',
        completed: state === 'loading_complete' || stop.isCompleted,
        inProgress: state === 'loading_in_progress',
        address: stop.address,
        detail: stop.address ?? undefined,
        contextLines: timelineContextLines(ctx)
      });
    }
    if (stop.loadingCompletedAt || state === 'loading_complete' || stop.isCompleted) {
      const loadedCtx = ctx
        ? { ...ctx, cargoLines: ctx.loadedLines.length ? ctx.loadedLines : ctx.cargoLines }
        : undefined;
      entries.push({
        key: `load-done-${stop.executionStopUid}`,
        label: 'Погрузка завершена',
        at: stop.loadingCompletedAt,
        kind: 'loading',
        completed: true,
        inProgress: false,
        address: stop.address,
        detail:
          loadedCtx && loadedCtx.totalPlaces > 0
            ? `${loadedCtx.totalPlaces} мест${loadedCtx.totalWeightKg > 0 ? ` · ${loadedCtx.totalWeightKg} кг` : ''}`
            : undefined,
        contextLines: timelineContextLines(loadedCtx, true)
      });
    }
  }

  if (routeStatus.allLoadingComplete) {
    entries.push({
      key: 'transit',
      label: 'Выезд по маршруту',
      at: null,
      kind: 'transit',
      completed: routeStatus.anyUnloadingStarted || routeStatus.key === 'in_transit',
      inProgress: routeStatus.key === 'in_transit' && !routeStatus.anyUnloadingStarted
    });
  }

  for (const incident of incidents) {
    entries.push({
      key: `incident-${incident.id}`,
      label:
        incident.incidentType === 'downtime'
          ? 'Зафиксирован простой'
          : `Инцидент: ${getIncidentTypeLabel(incident.incidentType)}`,
      at: incident.occurredAt,
      kind: 'incident',
      completed: incident.status === 'resolved',
      inProgress: incident.status === 'open',
      detail: formatIncidentSummary(incident)
    });
  }

  for (const stop of hydration.stops.filter((s) => !isLoadingStop(s.type))) {
    const state = normalizeOperationalState(stop.executionState);
    const ctx = stopContextMap.get(stop.executionStopUid);
    const stopCargo = hydration.cargo.filter((c) => c.toExecutionStopUid === stop.executionStopUid);
    const unloadedQty = stopCargo.reduce((s, c) => s + c.unloadedQty, 0);
    const cargoDetail = stopCargo.length > 0 ? `${unloadedQty} мест` : undefined;

    if (stop.unloadingStartedAt || state === 'unloading_in_progress') {
      entries.push({
        key: `unload-start-${stop.executionStopUid}`,
        label: 'Начата выгрузка',
        at: stop.unloadingStartedAt ?? stop.arrivedAt,
        kind: 'unloading',
        completed: state === 'unloading_complete' || stop.isCompleted,
        inProgress: state === 'unloading_in_progress',
        address: stop.address,
        detail: stop.address ?? undefined,
        contextLines: timelineContextLines(ctx)
      });
    }
    if (stop.unloadingCompletedAt || state === 'unloading_complete' || stop.isCompleted) {
      const doneCtx = ctx
        ? { ...ctx, cargoLines: ctx.loadedLines.length ? ctx.loadedLines : ctx.cargoLines }
        : undefined;
      entries.push({
        key: `unload-done-${stop.executionStopUid}`,
        label: 'Выгрузка завершена',
        at: stop.unloadingCompletedAt,
        kind: 'unloading',
        completed: true,
        inProgress: false,
        address: stop.address,
        detail: cargoDetail,
        contextLines: timelineContextLines(doneCtx, true)
      });
    }
  }

  if (routeStatus.key === 'completed') {
    entries.push({
      key: 'route-complete',
      label: 'Маршрут завершён',
      at: null,
      kind: 'milestone',
      completed: true,
      inProgress: false
    });
  }

  return entries.sort((a, b) => {
    const ta = a.at ? new Date(a.at).getTime() : 0;
    const tb = b.at ? new Date(b.at).getTime() : 0;
    return ta - tb;
  });
}

const INTERNAL_EVENT_TYPES = new Set([
  'PIPELINE_TRACE',
  'SESSION_STATE_TRANSITION',
  'PIPELINE_SNAPSHOT',
  'EXECUTION_PROGRESS_UPDATE',
  'ROUTE_STATUS_PROJECTION',
  'FINANCE_READINESS_TRANSITION'
]);

const EVENT_TYPE_LABELS: Record<string, string> = {
  STOP_EXECUTION_TRANSITION: 'Изменение статуса точки',
  CARGO_EXECUTION_TRANSITION: 'Изменение статуса груза',
  EXECUTION_COMPLETED: 'Маршрут завершён',
  WORKFLOW_TRIGGER_LOADING_COMPLETE: 'Документы после погрузки',
  WORKFLOW_TRIGGER_UNLOADING_COMPLETE: 'Документы после выгрузки',
  ALLOCATIONS_SAVED: 'Грузы распределены'
};

export function labelOperationalEvent(event: ExecutionEventRow): string | null {
  if (INTERNAL_EVENT_TYPES.has(event.eventType)) return null;

  const payload = event.payload;
  const obj =
    payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};

  if (event.eventType === 'STOP_EXECUTION_TRANSITION') {
    const to = obj.to ?? obj.executionState;
    if (to) return `Точка: ${getExecutionStatusLabel(String(to))}`;
    return 'Изменение статуса точки';
  }

  if (event.eventType === 'CARGO_EXECUTION_TRANSITION') {
    const to = obj.executionState;
    if (to) return `Груз: ${getExecutionStatusLabel(String(to))}`;
    return 'Изменение статуса груза';
  }

  if (event.eventType === 'EXECUTION_INCIDENT_CREATED') {
    const type = obj.incidentType ? getIncidentTypeLabel(String(obj.incidentType) as never) : 'Инцидент';
    return `Зафиксирован инцидент: ${type}`;
  }
  if (event.eventType === 'EXECUTION_INCIDENT_RESOLVED') return 'Инцидент закрыт';

  if (event.eventType === 'EXECUTION_COMPLETED') return 'Маршрут завершён';
  if (event.eventType === 'ALLOCATIONS_SAVED') return 'Грузы распределены';

  return EVENT_TYPE_LABELS[event.eventType] ?? null;
}

export function filterOperationalEvents(events: ExecutionEventRow[]): Array<{
  id: number;
  label: string;
  at: string;
}> {
  return events
    .map((event) => {
      const label = labelOperationalEvent(event);
      if (!label) return null;
      return { id: event.id, label, at: event.createdAt };
    })
    .filter((e): e is { id: number; label: string; at: string } => e != null);
}
